import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SupervisorService } from '../../services/supervisorService';
import { BarChart3, FileText, Users, TrendingUp, Download, Calendar } from 'lucide-react';

const SupervisorOperations = () => {
  const { data: operationsDataResult, isLoading } = useQuery({
    queryKey: ['supervisorOperations'],
    queryFn: () => SupervisorService.getOperationsData(),
  });
  const operationsData = operationsDataResult?.data || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Operations</h1>
        <p className="text-gray-600 mt-1">Monitor operations and team performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Active Teams</p>
            <Users size={20} className="text-violet-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{operationsData.activeTeams || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Tasks</p>
            <BarChart3 size={20} className="text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{operationsData.totalTasks || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Completed Today</p>
            <TrendingUp size={20} className="text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{operationsData.completedToday || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Pending Issues</p>
            <FileText size={20} className="text-red-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{operationsData.pendingIssues || 0}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Activity {i}</p>
                <p className="text-sm text-gray-600">Activity description</p>
              </div>
              <span className="text-sm text-gray-500">{new Date().toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SupervisorReports = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['supervisorReports', dateRange],
    queryFn: () => SupervisorService.getReports(dateRange),
  });
  const reports = reportsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600 mt-1">Generate and view reports</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="End Date"
          />
          <button className="px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 flex items-center space-x-2">
            <Download size={18} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No reports found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generated</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">#{report.id?.slice(0, 8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{report.type || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{report.period || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{report.created_at ? new Date(report.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button className="px-3 py-1 bg-violet-500 text-white rounded-lg hover:bg-violet-600 text-sm">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export { SupervisorOperations, SupervisorReports };